// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilValue } from 'recoil';

// Import Pages
import Preview from '../Preview/Preview';
import Transform from '../Transform/Transform';
import Assign from '../Assign/Assign';

// Import Material UI
import { Dialog, Button, makeStyles, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Import Icons
import CloseIcon from '../../../../../assets/icons/close-icon.svg';
import PassiveRadioIcon from '../../../../../assets/icons/passive-radio-icon.svg';
import ActiveRadioIcon from '../../../../../assets/icons/active-radio-icon.svg';

// Import Variable Icons
import ArrayIcon from '../../../../../assets/icons/variableIcons/array';
import DataTableIcon from '../../../../../assets/icons/variableIcons/dataTable';
import DateIcon from '../../../../../assets/icons/variableIcons/date';
import DictionaryIcon from '../../../../../assets/icons/variableIcons/dictionary';
import NumberIcon from '../../../../../assets/icons/variableIcons/number';
import ObjectIcon from '../../../../../assets/icons/variableIcons/object';
import StringIcon from '../../../../../assets/icons/variableIcons/string';
import BooleanIcon from '../../../../../assets/icons/variableIcons/boolean';
import VariableIcon from '../../../../../assets/icons/moduleIcons/variables-icon.svg';

// Import Helpers
import { findActionById, isActionValueController, getVariableIcon } from "../../../../../helpers/helper";

// Import Recoil
import { alertState, baseOrigin } from '../../../../../recoil-store/recoil-state';

// Import Services
import AutomService from "../../../../../services/AutomService/AutomService";
import VariableService from "../../../../../services/VariableService/VariableService";
import ActionService from "../../../../../services/ActionService/ActionService";

// Import CSS
import '../dataTransform.css'

const useStyles = makeStyles(theme => ({
    dialogContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        boxShadow: 'none',
        background: 'transparent',
        overflowY: "visible"
    },
    dialogPaper: {
        boxShadow: 'none', // Kutu gölgesini kaldır
        backgroundColor: 'transparent', // Arkaplanı şeffaf yap
    },
    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    },
    inputRoot: {
        "& fieldset": {
            borderColor: '#E6E6E6',
            borderWidth: '1.1px'
        }
    },
    popupIndicator: {
        color: "#B0B0B0",
        height: "10px",
        width: "10px"
    }
}));

const fetchActionList = [
    { id: 1, library_name: "RESTFUL-WEB-SERVICE", action_names: ["GET-METHOD", "POST-METHOD"], output_field_name: 'set', response_type: "any" },
    { id: 2, library_name: "DATABASE", action_names: ["SELECT"], output_field_name: 'set', response_type: "data table", depends: { library_name: "DATABASE", action_name: "CONNECT" } },
    { id: 3, library_name: "EXCEL", action_names: ["READ-FILE"], output_field_name: '_set', response_type: "data table" },
    { id: 4, library_name: "CSV", action_names: ["READ"], output_field_name: 'set', response_type: "data table" },
    { id: 5, library_name: "WORD", action_names: ["READ-DOCUMENT"], output_field_name: 'set', response_type: "array" },
    { id: 6, library_name: "TEXT", action_names: ["TEXT-READ"], output_field_name: 'set', response_type: "string" }
    // { id: 7, library_name: "ACTIVE-DIRECTORY", action_names: ["LDAP-QUERY"], output_field_name: 'output', response_type: "array", depends: { library_name: "ACTIVE-DIRECTORY", action_name: "CONNECT" } }
];

function BaseModal({ item_id, baseAction, cancel, variables, setOpenVariable, variableRender, automDetail, actions, afterSave }) {
    const [loading, setLoading] = useState(false);
    const [sourceDataType, setSourceDataType] = useState('variable');

    const [previousActions, setPreviousActions] = useState([]);
    const [dependsActions, setDependsActions] = useState([]);

    const [selectedVariable, setSelectedVariable] = useState({});
    const [selectedPreviousAction, setSelectedPreviousAction] = useState({});
    const [selectedDependsAction, setSelectedDependsAction] = useState({});

    const [selectedOutputVariable, setSelectedOutputVariable] = useState("");
    const [openModalName, setOpenModalName] = useState("manipulation");

    const [transforms, setTransforms] = useState([]);
    const [transformData, setTransformData] = useState({});

    const setAlert = useSetRecoilState(alertState);

    const [previewData, setPreviewData] = useState({ data: undefined, type: '', output_variables: [] });

    const base_origin = useRecoilValue(baseOrigin);
    const { t } = useTranslation();

    const classes = useStyles();
    const label = "CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.DATA-TRANSFORM.";

    useEffect(() => {
        const getAutomDetail = async () => {
            let a = [];
            let items = JSON.parse(JSON.stringify(automDetail.items))
            await findPreviousActions(a, items);
            setPreviousActions(a);
            if (baseAction.fields.data.value.sourceDataType) {
                let data = JSON.parse(JSON.stringify(baseAction.fields.data.value));

                setSourceDataType(data.sourceDataType);
                if (data.sourceDataType === "variable") {
                    let v = variables.find(x => x.id === data.variableId);
                    if (!v) {
                        defaultManipulation();
                        return setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(label + "VARIABLE-NOT-FOUND"),
                            severity: 'error'
                        }));

                    }
                    changeVariable(v);
                } else {
                    let pre = a.find((x) => x.item_id === data.previousActionItemId);
                    if (!pre) {
                        defaultPreview();
                        return setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(label + "ACTION-NOT-FOUND"),
                            severity: 'error'
                        }));
                    }
                    setSelectedPreviousAction(pre);

                    if (pre.depends) {
                        let d = [];
                        let items = JSON.parse(JSON.stringify(automDetail.items));
                        await findDependsActions(d, items, pre.depends, pre.item_id);
                        setDependsActions(d);

                        let dep = d.find((x) => x.item_id === data.dependsActionItemId);
                        if (!dep) {
                            defaultPreview();
                            return setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: t(label + "ACTION-NOT-FOUND"),
                                severity: 'error'
                            }));
                        }
                        setSelectedDependsAction(dep);

                        setLoading(true);

                        await fetchData(pre, dep.item_id, data.outputVariable);

                        setLoading(false);


                    } else {
                        setLoading(true);

                        await fetchData(pre, null, data.outputVariable)// v.item_id, v.response_type);
                        setLoading(false);
                    }
                }
                setTransforms(data.transforms);
            }
        }

        getAutomDetail();
    }, [])

    const defaultPreview = () => {
        setPreviewData({ data: undefined, type: '', output_variables: [] });
        setSelectedOutputVariable("");
    }

    const defaultManipulation = () => {
        defaultPreview();
        setSelectedPreviousAction({});
        setSelectedDependsAction({});
        setSelectedOutputVariable("");
        setSelectedVariable({});
        setTransformData({});
        setTransforms([]);
    }

    const makeActionNameUnique = (a, name) => {
        try {
            // Checking if there is an item with the same name
            const f = a.filter(x => x.name.includes(name))
            if (f.length) {
                let num = 1;
                // found the largest number at the end of the name
                for (const it of f) {
                    const y = it.name.split(/[()]/)[1];
                    if (y) {
                        let n = parseInt(y)
                        if (n > num) {
                            num = n;
                        }
                    }
                }
                // number added to name
                name += ` (${num + 1})`
            }
            return name;
        } catch (error) {
            return name;
        }
    }

    const findPreviousActions = (a, items) => {
        return new Promise(async (resolve) => {
            let end = false;
            for (const item of items) {
                if (item.item_id === item_id) {
                    end = true;
                    break;
                }
                let actionOrjName;
                const obj = fetchActionList.find(x => x.library_name === item.libraryName);
                if (item.library_label && item.library_label.includes('CUSTOM')) {
                    actionOrjName = item.library_name + " - " + item.name;
                } else if (obj) {
                    if (obj.action_names.find(x => x === item.actionName)) {
                        actionOrjName = t(`CONTENT.LIBRARY.${item.libraryName}.NAME`) + " - " + t(`CONTENT.LIBRARY.${item.libraryName}.ACTION.${item.name}`);
                    }
                }

                if (actionOrjName) {
                    let name = item.label ? item.label :
                        actionOrjName;
                    name = makeActionNameUnique(a, name);
                    let depends = obj ? obj.depends : null;
                    let response_type = "any";
                    // If the output_field_name field is empty, this action is a custom action.
                    let output_field_name = "";
                    if (obj) {
                        response_type = obj.response_type;
                        output_field_name = obj.output_field_name;
                    }
                    a.push({
                        action_id: item.actionId, item_id: item.item_id, name, actionOrjName, image: item.image,
                        image_path: item.image_path ? item.image_path : '', depends, response_type, output_field_name
                    })
                }

                if (item.children && item.children.length) {
                    const rsp = await findPreviousActions(a, item.children);
                    if (rsp) {
                        end = true;
                        break;
                    }
                }
            }
            resolve(end);
        })
    }

    const findDependsActions = (a, items, deps, itemId) => {
        return new Promise(async (resolve) => {
            let end = false;
            for (const item of items) {
                if (item.item_id === itemId) {
                    end = true;
                    break;
                }
                if (deps.library_name === item.libraryName) {
                    if (deps.action_name === item.actionName) {
                        let actionOrjName = t(`CONTENT.LIBRARY.${item.libraryName}.NAME`) + " - " + t(`CONTENT.LIBRARY.${item.libraryName}.ACTION.${item.name}`);

                        let name = item.label ? item.label :
                            actionOrjName;
                        name = makeActionNameUnique(a, name);
                        a.push({ item_id: item.item_id, name, actionOrjName, image: item.image, image_path: item.image_path ? item.image_path : '' })
                    }
                }

                if (item.children && item.children.length) {
                    const rsp = await findDependsActions(a, item.children, deps, itemId);
                    if (rsp) {
                        end = true;
                        break;
                    }
                }
            }
            resolve(end);
        })
    }

    const getCustomActionOutput = async (action_id, item_id, versionId) => {
        let response = { v: '', response_type: '' };
        const result = await ActionService.getCustomActionOutputFields(action_id, item_id, automDetail.versionId);
        if (result.code !== 200) {
            defaultPreview();
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: result.message,
                severity: 'error'
            }))
        }

        for (const item of result.data) {
            response.v = item.output;
            break;
        }

        if (response.v) {
            if (Array.isArray(response.v)) {
                if (response.v.some(element => Array.isArray(element))) {
                    response.response_type = "data table";
                } else {
                    response.response_type = "array";
                }
            } else {
                response.response_type = typeof response.v;
            }
        }

        return response;
    }

    const changeVariable = async (v, resetTransform) => {
        if (resetTransform) {
            setTransforms([]);
        }
        setSelectedVariable(v ? v : {});
        if (v?.related_item_id) {
            let actionDetail = await checkAndGetAction(v.related_item_id, "VARIABLE-ACTION-NOT-FOUND", true);
            if (!actionDetail) {
                return
            }
            const res = await getCustomActionOutput(actionDetail.actionId, v.related_item_id);
            if (res.v) {
                setPreviewData({ data: res.v, type: res.response_type, output_variables: v.name });
                setSelectedOutputVariable(v.name);
                setTransformData({});
                return;
            }

        }

        if (v) {
            let data = v.default_value;
            if (v.default_value) {
                if (["object", "array", "data table"].find(x => x === v.type)) {
                    try {
                        data = JSON.parse(data)
                    } catch (_) {
                        defaultPreview();
                        return setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(label + "VALUE-FORMAT-ERROR"),
                            severity: 'error'
                        }));
                    }
                }
            }

            setPreviewData({ data, type: v.type, output_variables: v.name });
            setSelectedOutputVariable(v.name);
            setTransformData({});
        } else {
            defaultPreview();
        }

    }



    // a => action
    const fetchData = async (ac, dependentId, output) => {

        let actionDetail = await checkAndGetAction(ac.item_id);
        if (!actionDetail) {
            return
        }

        actionDetail.automId = automDetail.id;
        actionDetail.automVersionId = automDetail.versionId;
        if (dependentId) {
            let d = await checkAndGetAction(dependentId);
            if (!d) {
                return
            }
            actionDetail.dependentActions = [d];
        }
        let response = await AutomService.runAction(actionDetail);
        if (response) {
            if (response.message.includes("System Error:")) {
                defaultPreview();
                let m = response.message.replace("System Error:", "");
                m = m.replace(" - For detailed information check out the Log & Advanced Log from Monitoring.", "")

                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: m,
                    severity: 'error'
                }))
            } else {
                if (response.status) {
                    let v = response.dataString;
                    if (ac.response_type !== "string") {
                        if (response.dataString) {
                            try {
                                v = JSON.parse(response.dataString);
                            } catch (_) { }

                            if (ac.response_type === "any") {
                                if (Array.isArray(v)) {
                                    if (v.some(element => Array.isArray(element))) {
                                        ac.response_type = "data table";
                                    } else {
                                        ac.response_type = "array";
                                    }
                                } else {
                                    ac.response_type = typeof v;
                                }
                            }
                        }
                    }

                    let variable_names;

                    if (!ac.output_field_name) {
                        const result = await ActionService.getCustomActionOutputFields(ac.action_id, ac.item_id, automDetail.versionId);
                        if (result.code !== 200) {
                            defaultPreview();
                            return setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: result.message,
                                severity: 'error'
                            }))
                        }

                        for (const item of result.data) {
                            if (actionDetail.fields && actionDetail.fields[item.field_name]) {
                                variable_names = [actionDetail.fields[item.field_name].value];
                                v = item.output;
                                break;
                            }
                        }
                        if (!variable_names) {

                        }

                        if (v) {
                            if (Array.isArray(v)) {
                                if (v.some(element => Array.isArray(element))) {
                                    ac.response_type = "data table";
                                } else {
                                    ac.response_type = "array";
                                }
                            } else {
                                ac.response_type = typeof v;
                            }
                        }

                    } else {
                        if (ac.output_field_name === "_set") {

                            let names = [];
                            for (let key of Object.keys(actionDetail.fields)) {
                                if (key.includes('_set') && actionDetail.fields[key] && actionDetail.fields[key].value) {
                                    let variableName = actionDetail.fields[key].value;
                                    names.push(variableName);
                                    // if (names.length === 0 && v[variableName]) {
                                    //     v = v[variableName];
                                    // }
                                }
                            }
                            if (!names.length) {
                                defaultPreview();
                                return setAlert(alert => ({
                                    ...alert,
                                    show: true,
                                    message: t('ACTIVE-DIRECTORY.NOT-NULL'),
                                    severity: 'error'
                                }))
                            }
                            variable_names = names;
                        } else if (actionDetail.fields && actionDetail.fields[ac.output_field_name]) {
                            variable_names = [actionDetail.fields[ac.output_field_name].value];
                        } else {
                            defaultPreview();
                            return setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: label + "OUTPUT-VARIABLE-NOT-FOUND",
                                severity: 'error'
                            }))
                        }
                    }

                    setPreviewData({ data: v, type: ac.response_type, output_variables: variable_names });
                    if (output) {
                        if (variable_names.find(x => x === output)) {
                            setSelectedOutputVariable(output);
                            return;
                        }
                    }
                    setSelectedOutputVariable(variable_names[0]);
                } else {
                    defaultPreview();
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t('FLOW-MESSAGES.LIBRARY.' + response.message),
                        severity: 'error'
                    }))
                }
            }
        }

    }


    const changePreviousAction = async (v) => {

        setTransforms([]);
        setSelectedPreviousAction(v ? v : {});
        if (v) {
            if (v.depends) {
                setPreviewData({ data: undefined, type: '' });
                let a = [];
                let items = JSON.parse(JSON.stringify(automDetail.items));
                await findDependsActions(a, items, v.depends, v.item_id);
                setDependsActions(a);
            } else {
                setLoading(true);

                await fetchData(v)
                setLoading(false);
            }
        } else {
            defaultPreview();
        }
    }

    const changeOutputVariables = (v) => {
        setSelectedOutputVariable(v ? v : {});
    }

    const checkAndGetAction = async (itemId, notFoundMessage, noFieldControl) => {
        let items = JSON.parse(JSON.stringify(automDetail.items))
        let a = await findActionById(itemId, items)
        if (!a || !actions[a?.id]) {
            defaultPreview();
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + (notFoundMessage ? notFoundMessage : "ACTION-NOT-FOUND")),
                severity: 'error'
            }))
            return;
        }
        a.fields = actions[a?.id].fields;
        let res = isActionValueController(a.fields);
        if (res.indexOf(false) === -1 || noFieldControl) {
            return a;
        } else {
            defaultPreview();
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('ACTIVE-DIRECTORY.NOT-NULL'),
                severity: 'error'
            }))
        }
        return;
    }

    const changeDependAction = async (v) => {
        setTransforms([]);
        setSelectedDependsAction(v ? v : {});
        if (v) {
            setLoading(true);
            let a = await checkAndGetAction(selectedPreviousAction.item_id);
            if (a) {
                await fetchData(selectedPreviousAction, v.item_id);
            }
            setLoading(false);
        } else {
            defaultPreview();
        }
    }


    const changeRadioButton = (v) => {
        defaultManipulation();
        setSourceDataType(v);
    }

    // const getVariableIcon = (name, classes, fill) => {
    //     switch (name) {
    //         case "array":
    //             return <ArrayIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         case "boolean":
    //             return <BooleanIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         case "data table":
    //             return <DataTableIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         case "date":
    //             return <DateIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         case "dictionary":
    //             return <DictionaryIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         case "number":
    //             return <NumberIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         case "object":
    //             return <ObjectIcon fill={fill} className={`select-variable-icon ${classes}`} />//<img className={`select-variable-icon ${classes}`} src={ObjectIcon} alt="action control icon" />
    //         case "string":
    //             return <StringIcon fill={fill} className={`select-variable-icon ${classes}`} />
    //         default:
    //             return undefined
    //     }
    // }

    const reFetchData = async () => {
        setLoading(true);
        if (sourceDataType === "variable") {
            if (!selectedVariable.id) {
                setAlert(alert => ({
                    ...alert, show: true,
                    message: t(label + "SELECT-VARIABLE"),
                    severity: 'error'
                }));
            } else {
                const result = await VariableService.getByVariable(selectedVariable.id);
                if (result && result.status) {

                    if (result.data.related_item_id) {
                        let actionDetail = await checkAndGetAction(result.data.related_item_id, "VARIABLE-ACTION-NOT-FOUND", true);
                        if (!actionDetail) {
                            setLoading(false);
                            return
                        }
                        const res = await getCustomActionOutput(actionDetail.actionId, result.data.related_item_id);
                        if (res.v) {
                            setPreviewData({ data: res.v, type: res.response_type, output_variables: result.data.name });
                            setSelectedOutputVariable(result.data.name);
                            setLoading(false);
                            return;
                        }

                    }

                    let data = result.data.default_value;
                    if (result.data.type) {
                        if (result.data.default_value) {
                            if (["object", "array", "data table"].find(x => x === result.data.type)) {
                                try {
                                    data = JSON.parse(data)
                                } catch (_) {
                                    setLoading(false);
                                    return setAlert(alert => ({
                                        ...alert, show: true,
                                        message: t(label + "VALUE-FORMAT-ERROR"),
                                        severity: 'error'
                                    }));
                                }
                            }
                        }
                        setPreviewData({ data, type: result.data.type, output_variables: result.data.name });
                    } else {
                        defaultPreview();
                    }
                } else {
                    defaultPreview();
                    setAlert(alert => ({
                        ...alert, show: true,
                        message: result ? result.message : "GENERAL.NETWORK-ERROR",
                        severity: 'error'
                    }));
                }
            }
        } else if (sourceDataType === "fetch") {
            if (!selectedPreviousAction.item_id) {
                setAlert(alert => ({
                    ...alert, show: true,
                    message: t(label + "SELECT-PREVIOUS-ACTION"),
                    severity: 'error'
                }));
            } else if (selectedPreviousAction.depends) {
                if (!selectedDependsAction.item_id) {
                    setAlert(alert => ({
                        ...alert, show: true,
                        message: t(label + "SELECT-DEPENDS-ACTION"),
                        severity: 'error'
                    }));
                } else {
                    await fetchData(selectedPreviousAction, selectedDependsAction.item_id);
                }
            } else {
                await fetchData(selectedPreviousAction);
            }
        }

        setLoading(false);
    }

    const onChangeTransformData = (path, type) => {
        if (path) {
            let t = transforms.find(x => x.path === path);
            if (t) {
                setTransformData({
                    showPage: 'transform', path, type: type ? type : t.type,
                    assignType: t.assignType,
                    diffrentPaths: t.diffrentPaths
                });
            } else {
                setTransformData({ showPage: 'assign', path, type });
            }
        } else {
            setTransformData({});
        }
    }

    const saveTransform = (manipulations, data) => {
        if (openModalName !== "manipulation") {
            setOpenModalName("manipulation");
        }

        setTransforms(prev => {
            let m = [...prev];
            let i = m.findIndex(x => x.path === data.path);
            if (i !== -1) {
                m[i] = { path: data.path, type: data.type, assignType: data.assignType, diffrentPaths: data.diffrentPaths, manipulations }
            } else {
                m.push({ path: data.path, type: data.type, assignType: data.assignType, diffrentPaths: data.diffrentPaths, manipulations });
            }
            return m;
        });

        setTransformData({})
    }

    const cancelTransform = () => {
        if (openModalName !== "manipulation") {
            setOpenModalName("manipulation");
        }

        setTransformData({})
    }

    const saveAssign = (assignType, diffrentPaths) => {

        setTransformData({
            ...transformData,
            showPage: 'transform',
            assignType,
            diffrentPaths
        })

    }



    const removeTransform = (pathToRemove) => {
        if (openModalName !== "manipulation") {
            setOpenModalName("manipulation");
        }

        setTransforms(prev => {
            let m = [...prev];
            return m.filter(x => x.path !== pathToRemove);
        });

        setTransformData({})
    }

    const resetTransforms = () => {
        setTransformData({});
        setTransforms([]);
    }

    const clearTransformData = () => {
        setTransformData({});
    }

    const completeManipulation = () => {
        if (!transforms || !transforms.length) {
            return setAlert(alert => ({
                ...alert, show: true,
                message: t(label + "TRANSFORM-NOT-VALID"),
                severity: 'error'
            }));
        }

        let variableId = "";
        let previousActionItemId = "";
        let dependsActionItemId = "";
        let outputVariable = "";
        if (sourceDataType === "variable") {
            variableId = selectedVariable.id;
            if (!variableId) {
                return setAlert(alert => ({
                    ...alert, show: true,
                    message: t(label + "VARIABLE-NOT-SELECTED"),
                    severity: 'error'
                }));
            }
        } else {
            previousActionItemId = selectedPreviousAction.item_id;
            if (!previousActionItemId) {
                return setAlert(alert => ({
                    ...alert, show: true,
                    message: t(label + "PREVIOUS-ACTION-NOT-SELECTED"),
                    severity: 'error'
                }));
            }

            if (selectedDependsAction?.item_id) {
                dependsActionItemId = selectedDependsAction?.item_id;
            }
        }

        if (selectedOutputVariable) {
            outputVariable = selectedOutputVariable;
            let v = variables.find(x => x.name === selectedOutputVariable);
            if (v) {
                variableId = v.id;
            }
        }

        baseAction.fields.data.value = {
            sourceDataType: sourceDataType,
            sourceVariableType: previewData.type,
            variableId,
            previousActionItemId,
            dependsActionItemId,
            outputVariable,
            transforms: transforms
        };

        afterSave();
    }

    return (
        <Dialog open={true} className='base-dialog-content' maxWidth={850} PaperProps={{ className: classes.dialogContainer }}>
            <div className='transform-page-container'>
                {/* Left Side Data Manipulation Start*/}
                {
                    openModalName !== "manipulation" ? <div style={{ width: "408px" }}></div>
                        :
                        <div className='left-transform-page transform-page'>
                            <div className='items-center-between'>
                                <div className='base-modal-title base-title-detail'>
                                    {t(label + "DATA-MANIPULATION")}
                                </div>
                                <img className='transform-cancel-icon' src={CloseIcon} onClick={cancel} />
                            </div>

                            {/*Variable radio button start*/}
                            <div className={`radio-button-background choose-variable-container ${sourceDataType === 'variable' && 'active'}`}>
                                <div className='just-display-flex'>
                                    <img className='transform-radio-icon' src={sourceDataType === 'variable' ? ActiveRadioIcon : PassiveRadioIcon}
                                        onClick={() => changeRadioButton('variable')} />

                                    <div className='choose-variable-content'>
                                        <div className='base-title-detail modal-sub-title '>
                                            {t(label + "CHOOSE-VARIABLE")}
                                        </div>
                                        {sourceDataType === 'variable' &&
                                            <>
                                                <div className='modal-sub-description'>
                                                    {t(label + "CHOOSE-VARIABLE-DESC")}
                                                </div>
                                                <div className='items-center'>

                                                    <Autocomplete
                                                        value={selectedVariable}
                                                        onChange={(e, newValue) => changeVariable(newValue, true)}
                                                        id="data-transform-variable-list"
                                                        className='select-variable-css'
                                                        // style={{ width: 242, position: 'relative', background: '#FBFBFB', display: 'flex', alignItems: 'center' }}
                                                        size="small"
                                                        options={variables}
                                                        classes={{
                                                            option: classes.option,
                                                            inputRoot: classes.inputRoot,
                                                            popupIndicator: classes.popupIndicator
                                                        }}
                                                        autoHighlight
                                                        getOptionLabel={(option) => option.name ?? ""}
                                                        getOptionSelected={(option, value) => option.id === value.id}

                                                        renderOption={(option) => (
                                                            <div className='items-center position-r'>
                                                                {option.type && getVariableIcon(option.type, "")}
                                                                <div className='variable-name-text'>{option.name}</div>
                                                            </div>
                                                        )}
                                                        renderInput={(params) => (
                                                            <>
                                                                {
                                                                    selectedVariable.type && getVariableIcon(selectedVariable.type, "m-l-11")
                                                                }
                                                                {/* { 
                                                    selectedAction && selectedAction.image &&
                                                    <img className='input-action-icon' src={selectedAction.image_path ? `${base_origin}${selectedAction.image_path}` : selectedAction.image} />
                                                } */}
                                                                <TextField
                                                                    {...params}
                                                                    id='variable-list-text'
                                                                    className="select-action-input"
                                                                    placeholder="No Variable Selected"
                                                                    variant="outlined"
                                                                    classes={{
                                                                        root: classes.inputRoot
                                                                    }}
                                                                    inputProps={{
                                                                        ...params.inputProps,
                                                                        "aria-autocomplete": 'new-password',
                                                                        autoComplete: 'new-password', // disable autocomplete and autofill                                                                
                                                                        style: {
                                                                            marginLeft: '22px',
                                                                            fontSize: 12,
                                                                            fontWeight: 600,
                                                                            color: '#46474E'
                                                                        }
                                                                    }}
                                                                />
                                                            </>
                                                        )}
                                                    />
                                                    {/* <BluePlusIcon className='create-variable-icon' onClick={() => setOpenVariable(true)} /> */}
                                                    {/* <img className='create-variable-icon' src={BluePlusIcon} onClick={() => setOpenVariable(true)} /> */}
                                                </div>
                                            </>
                                        }

                                    </div>
                                </div>
                            </div>
                            {/* Variable radio button end*/}



                            {/* Previous action radio button start*/}
                            <div className={`radio-button-background fetch-previous-action-container ${sourceDataType === 'fetch' && 'active'}`}>
                                <div className='just-display-flex'>
                                    <img className='transform-radio-icon' src={sourceDataType === 'fetch' ? ActiveRadioIcon : PassiveRadioIcon} onClick={() => changeRadioButton('fetch')} />

                                    <div className='choose-variable-content'>
                                        <div className='base-title-detail modal-sub-title '>
                                            {t(label + "FETCH-DATA-TITLE")}
                                        </div>
                                        {sourceDataType === 'fetch' &&
                                            <>
                                                <div className='modal-sub-description'>
                                                    {t(label + "SELECT-ACTION-DESC")}
                                                </div>
                                                <div className='items-center'>

                                                    <Autocomplete
                                                        value={selectedPreviousAction}
                                                        onChange={(e, newValue) => changePreviousAction(newValue)}
                                                        id="data-transform-previous-action-list"
                                                        style={{ width: 242, position: 'relative', background: '#FBFBFB' }}
                                                        size="small"
                                                        disabled={loading}
                                                        options={previousActions}
                                                        classes={{
                                                            option: classes.option,
                                                            inputRoot: classes.inputRoot,
                                                            popupIndicator: classes.popupIndicator
                                                        }}
                                                        autoHighlight
                                                        getOptionLabel={(option) => option.name ?? ""}
                                                        getOptionSelected={(option, value) => option.item_id === value.item_id}
                                                        renderOption={(option) => (
                                                            <>
                                                                <img
                                                                    loading="lazy"
                                                                    width="20"
                                                                    src={option.image_path ? `${base_origin}${option.image_path}` : option.image}
                                                                    alt=""
                                                                    style={{ marginRight: '10px' }}
                                                                />
                                                                {option.name}
                                                            </>
                                                        )}
                                                        renderInput={(params) => (
                                                            <>
                                                                {
                                                                    selectedPreviousAction && selectedPreviousAction.image &&
                                                                    <img className='input-action-icon' src={selectedPreviousAction.image_path ? `${base_origin}${selectedPreviousAction.image_path}` : selectedPreviousAction.image} />
                                                                }
                                                                <TextField
                                                                    {...params}
                                                                    id='previous-action-list-text'
                                                                    className="select-action-input"
                                                                    placeholder="No Action Selected"
                                                                    variant="outlined"
                                                                    classes={{
                                                                        root: classes.inputRoot
                                                                    }}
                                                                    inputProps={{
                                                                        ...params.inputProps,
                                                                        "aria-autocomplete": 'new-password',
                                                                        autoComplete: 'new-password', // disable autocomplete and autofill
                                                                        style: {
                                                                            marginLeft: '36px',
                                                                            fontSize: 12,
                                                                            fontWeight: 600,
                                                                            color: '#46474E'
                                                                        }
                                                                    }}
                                                                />
                                                            </>
                                                        )}
                                                    />
                                                </div>

                                                {
                                                    selectedPreviousAction.depends && dependsActions &&
                                                    <div className='m-t-10'>
                                                        <div className='modal-sub-description'>
                                                            {t(label + "SELECT-DEPEND-DESC")}
                                                        </div>
                                                        <div className='items-center'>

                                                            <Autocomplete
                                                                value={selectedDependsAction}
                                                                onChange={(e, newValue) => changeDependAction(newValue)}
                                                                id="data-transform-depend-list"
                                                                style={{ width: 242, position: 'relative', background: '#FBFBFB' }}
                                                                size="small"
                                                                disabled={loading}
                                                                options={dependsActions}
                                                                classes={{
                                                                    option: classes.option,
                                                                    inputRoot: classes.inputRoot,
                                                                    popupIndicator: classes.popupIndicator
                                                                }}
                                                                autoHighlight
                                                                getOptionLabel={(option) => option.name ?? ""}
                                                                getOptionSelected={(option, value) => option.item_id === value.item_id}
                                                                renderOption={(option) => (
                                                                    <>
                                                                        <img
                                                                            loading="lazy"
                                                                            width="20"
                                                                            src={option.image_path ? `${base_origin}${option.image_path}` : option.image}
                                                                            alt=""
                                                                            style={{ marginRight: '10px' }}
                                                                        />
                                                                        {option.name}
                                                                    </>
                                                                )}
                                                                renderInput={(params) => (
                                                                    <>
                                                                        {
                                                                            selectedDependsAction && selectedDependsAction.image &&
                                                                            <img className='input-action-icon' src={selectedDependsAction.image_path ? `${base_origin}${selectedDependsAction.image_path}` : selectedDependsAction.image} />
                                                                        }
                                                                        <TextField
                                                                            {...params}
                                                                            id='depend-action-list-text'
                                                                            className="select-action-input"
                                                                            placeholder="Dependency Not Selected"
                                                                            variant="outlined"
                                                                            classes={{
                                                                                root: classes.inputRoot
                                                                            }}
                                                                            inputProps={{
                                                                                ...params.inputProps,
                                                                                "aria-autocomplete": 'new-password',
                                                                                autoComplete: 'new-password', // disable autocomplete and autofill
                                                                                style: {
                                                                                    marginLeft: '36px',
                                                                                    fontSize: 12,
                                                                                    fontWeight: 600,
                                                                                    color: '#46474E'
                                                                                }
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                {
                                                    previewData.output_variables && previewData.output_variables.length > 0 &&
                                                    <div className='m-t-10'>
                                                        <div className='modal-sub-description'>
                                                            {t(label + "OUTPUT-VARIABLE")}
                                                        </div>
                                                        <div className='items-center'>
                                                            <Autocomplete
                                                                value={selectedOutputVariable}
                                                                onChange={(e, newValue) => changeOutputVariables(newValue)}
                                                                id="data-transform-output_variable"
                                                                style={{ width: 242, position: 'relative', background: '#FBFBFB' }}
                                                                size="small"
                                                                disableClearable={true}
                                                                popupIcon={previewData.output_variables.length === 1 ? "" : undefined}
                                                                disabled={previewData.output_variables.length === 1}
                                                                options={previewData.output_variables}
                                                                classes={{
                                                                    option: classes.option,
                                                                    inputRoot: classes.inputRoot,
                                                                    popupIndicator: classes.popupIndicator
                                                                }}
                                                                autoHighlight
                                                                renderOption={(option) => (
                                                                    <>
                                                                        <div className='items-center position-r'>
                                                                            <img loading="lazy" width="20" src={VariableIcon} alt="action control icon" />
                                                                            <div className='variable-name-text'>{option}</div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                renderInput={(params) => (
                                                                    <>
                                                                        <img className='input-action-icon' loading="lazy" width="20" src={VariableIcon} alt="action control icon" />

                                                                        <TextField
                                                                            {...params}
                                                                            id='output-variable-list-text'
                                                                            className="select-action-input"
                                                                            placeholder="Output Variable Not Selected"
                                                                            variant="outlined"
                                                                            classes={{
                                                                                root: classes.inputRoot
                                                                            }}
                                                                            inputProps={{
                                                                                ...params.inputProps,
                                                                                "aria-autocomplete": 'new-password',
                                                                                autoComplete: 'new-password', // disable autocomplete and autofill
                                                                                style: {
                                                                                    marginLeft: '36px',
                                                                                    fontSize: 12,
                                                                                    fontWeight: 600,
                                                                                    color: '#46474E'
                                                                                }
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                }
                                            </>
                                        }

                                    </div>
                                </div>

                            </div>
                            {/* Previous action radio button end*/}


                            {/* Preview data start */}
                            {/* {Object.keys(transforms)} */}
                            <Preview previewData={previewData} loading={loading} reFetchData={reFetchData}
                                transformKeys={transforms.map(x => x.path)} resetTransforms={resetTransforms}
                                outputVariableName={selectedOutputVariable} onChangeTransformData={onChangeTransformData} transformData={transformData} clearTransformData={clearTransformData} />
                            {/* Preview data end */}

                            <div className='configuration-footer'>
                                <Button className='transform-submit-button' onClick={completeManipulation}>
                                    {t(label + 'COMPLETE-MANIPULATION')}
                                </Button>
                            </div>
                        </div>
                }

                {/* Left Side Data Manipulation End*/}

                {/* --------------------------------------- */}

                {/* Right Side Transform Data Start*/}
                {/* {transforms[transformData.path]} */}
                {
                    transformData?.showPage === "transform" && transformData?.path && (
                        <Transform baseAction={baseAction} transformDetail={transforms.find(x => x.path === transformData.path)} data={transformData}
                            variables={variables} setOpenVariable={setOpenVariable} variableRender={variableRender} removeTransform={removeTransform}
                            cancel={cancelTransform} save={saveTransform} automDetail={automDetail} openModalName={openModalName} setOpenModalName={setOpenModalName} />
                    )
                }

                {
                    transformData?.showPage === "assign" && transformData?.path && (
                        <Assign assignData={transformData} variables={variables} setOpenVariable={setOpenVariable} cancel={cancelTransform} save={saveAssign} />
                    )
                }
                {/* Right Side Transform Data End*/}

            </div>

        </Dialog>

    );
}

export default BaseModal;